import { FC } from 'react';
import { Box, Button, Typography, Svg } from '@altermaind/web/ui';

type PropsBase = {
  title: string;
  description: string;
  dataTestId?: string;
};

type PropsWithOptionalButton = {
  buttonLabel?: never;
  onClick?: never;
};

type PropsWithMandatoryButton = {
  buttonLabel: string;
  onClick: () => void;
};

type Props = PropsBase & (PropsWithOptionalButton | PropsWithMandatoryButton);

/**
 * AlertMessage component displays an alert with a title, description, and an optional button.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {string} props.title - The title of the alert message.
 * @param {string} props.description - The description of the alert message.
 * @param {string} props.buttonLabel - The label of the button. If not provided, the button will not be displayed.
 * @param {Function} props.onClick - The function to call when the button is clicked.
 * @param {string} [props.dataTestId] - The data-testid attribute.
 *
 * @example
 * <AlertMessage
 *   title="Warning"
 *   description="This is a warning message."
 *   buttonLabel="Dismiss"
 *   onClick={() => console.log('Button clicked')}
 * />
 *
 * @returns {JSX.Element} The rendered AlertMessage component.
 *
 */
export const AlertMessage: FC<Props> = ({
  title,
  description,
  dataTestId,
  buttonLabel,
  onClick,
}) => {
  return (
    <Box data-testid={dataTestId} py='m' textAlign='center'>
      <Svg name='info' fill='warning' size='m' />
      <Typography variant='special' mt='s' mb='xs'>
        {title}
      </Typography>
      <Box width='80%' style={{ margin: '0 auto' }}>
        <Typography variant='p2/light'>{description}</Typography>
      </Box>
      {buttonLabel && (
        <Box display='flex' width='100%' mt='l'>
          <Button style={{ margin: '0 auto' }} variant='primary' onClick={onClick}>
            {buttonLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
};
